import React from "react";
import styled from "styled-components";
import { colors, typography } from "../styles/designSystem";

// ===== Shared Styles =====
const DiagramContainer = styled.div`
    background: ${({ theme }) => theme.cardBackground || theme.background};
    border: 1px solid ${({ theme }) => theme.borderLight};
    border-radius: 12px;
    padding: 2rem;
    width: 100%;
    overflow-x: auto;
`;

const DiagramLabel = styled.span`
    font-size: ${typography.fontSize[100]};
    font-weight: ${typography.fontWeight.semibold};
    text-transform: uppercase;
    letter-spacing: 0.08em;
    color: ${({ theme }) => theme.subtleText};
`;

// ===== 1. Component Hierarchy Diagram =====
const HierarchyWrapper = styled(DiagramContainer)`
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 1rem;
    padding: 2.5rem 2rem;
`;

const HierarchyLevel = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 0.5rem;
    width: 100%;
`;

const HierarchyBox = styled.div`
    background: ${({ color }) => color}15;
    border: 2px solid ${({ color }) => color};
    border-radius: 8px;
    padding: 0.75rem 1.5rem;
    text-align: center;
    min-width: 140px;
    position: relative;
`;

const HierarchyBoxTitle = styled.div`
    font-weight: ${typography.fontWeight.bold};
    font-size: ${typography.fontSize[200]};
    color: ${({ color }) => color};
    margin-bottom: 2px;
`;

const HierarchyBoxSub = styled.div`
    font-size: ${typography.fontSize[100]};
    color: ${({ theme }) => theme.subtleText};
`;

const HierarchyChildren = styled.div`
    display: flex;
    gap: 1.5rem;
    flex-wrap: wrap;
    justify-content: center;
`;

const Connector = styled.div`
    width: 2px;
    height: 20px;
    background: ${({ theme }) => theme.borderMedium};
`;

const BranchConnector = styled.div`
    display: flex;
    align-items: flex-start;
    justify-content: center;
    position: relative;
    width: 100%;

    &::before {
        content: '';
        position: absolute;
        top: 0;
        left: 25%;
        right: 25%;
        height: 2px;
        background: ${({ theme }) => theme.borderMedium};

        @media (max-width: 768px) {
            left: 10%;
            right: 10%;
        }
    }
`;

export const ComponentHierarchy = () => (
    <HierarchyWrapper>
        <DiagramLabel>Composition Hierarchy</DiagramLabel>

        <HierarchyLevel>
            <HierarchyBox color={colors.indigo[500]}>
                <HierarchyBoxTitle color={colors.indigo[500]}>Block</HierarchyBoxTitle>
                <HierarchyBoxSub>HeroBlock</HierarchyBoxSub>
            </HierarchyBox>
            <Connector />
        </HierarchyLevel>

        <BranchConnector />

        <HierarchyChildren>
            <HierarchyLevel>
                <Connector />
                <HierarchyBox color={colors.cyan[500]}>
                    <HierarchyBoxTitle color={colors.cyan[500]}>Pattern</HierarchyBoxTitle>
                    <HierarchyBoxSub>SearchBar</HierarchyBoxSub>
                </HierarchyBox>
                <Connector />
                <HierarchyChildren>
                    <HierarchyBox color={colors.emerald[500]}>
                        <HierarchyBoxTitle color={colors.emerald[500]}>Component</HierarchyBoxTitle>
                        <HierarchyBoxSub>Input</HierarchyBoxSub>
                    </HierarchyBox>
                    <HierarchyBox color={colors.emerald[500]}>
                        <HierarchyBoxTitle color={colors.emerald[500]}>Component</HierarchyBoxTitle>
                        <HierarchyBoxSub>Button</HierarchyBoxSub>
                    </HierarchyBox>
                </HierarchyChildren>
            </HierarchyLevel>

            <HierarchyLevel>
                <Connector />
                <HierarchyBox color={colors.cyan[500]}>
                    <HierarchyBoxTitle color={colors.cyan[500]}>Pattern</HierarchyBoxTitle>
                    <HierarchyBoxSub>CTARow</HierarchyBoxSub>
                </HierarchyBox>
                <Connector />
                <HierarchyChildren>
                    <HierarchyBox color={colors.emerald[500]}>
                        <HierarchyBoxTitle color={colors.emerald[500]}>Component</HierarchyBoxTitle>
                        <HierarchyBoxSub>Button</HierarchyBoxSub>
                    </HierarchyBox>
                    <HierarchyBox color={colors.emerald[500]}>
                        <HierarchyBoxTitle color={colors.emerald[500]}>Component</HierarchyBoxTitle>
                        <HierarchyBoxSub>Link</HierarchyBoxSub>
                    </HierarchyBox>
                </HierarchyChildren>
            </HierarchyLevel>
        </HierarchyChildren>
    </HierarchyWrapper>
);


// ===== 2. Variant Explosion vs Property-Based =====
const ComparisonWrapper = styled(DiagramContainer)`
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 2.5rem;
    padding: 2.5rem;

    @media (max-width: 768px) {
        grid-template-columns: 1fr;
        gap: 2rem;
    }
`;

const ComparisonSide = styled.div`
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
`;

const ComparisonHeader = styled.div`
    display: flex;
    align-items: center;
    gap: 0.5rem;
    margin-bottom: 0.5rem;
`;

const StatusDot = styled.div`
    width: 10px;
    height: 10px;
    border-radius: 50%;
    background: ${({ color }) => color};
`;

const ComparisonTitle = styled.span`
    font-weight: ${typography.fontWeight.bold};
    font-size: ${typography.fontSize[200]};
    color: ${({ color }) => color};
`;

const VariantItem = styled.div`
    background: ${({ theme }) => theme.mode === 'dark' ? 'rgba(255,107,107,0.08)' : 'rgba(255,107,107,0.06)'};
    border: 1px solid ${colors.coral[400]}40;
    border-radius: 4px;
    padding: 0.5rem 0.75rem;
    font-family: ${typography.fontFamily.code};
    font-size: ${typography.fontSize[100]};
    color: ${({ theme }) => theme.text};
`;

const PropComponentBox = styled.div`
    background: ${({ theme }) => theme.mode === 'dark' ? 'rgba(16,185,129,0.08)' : 'rgba(16,185,129,0.06)'};
    border: 2px solid ${colors.emerald[500]};
    border-radius: 8px;
    padding: 1.25rem;
`;

const PropComponentName = styled.div`
    font-family: ${typography.fontFamily.code};
    font-weight: ${typography.fontWeight.bold};
    font-size: ${typography.fontSize[300]};
    color: ${colors.emerald[500]};
    margin-bottom: 1rem;
`;

const PropRow = styled.div`
    display: flex;
    align-items: center;
    gap: 0.5rem;
    padding: 0.5rem 0;
    border-bottom: 1px solid ${({ theme }) => theme.borderLight};

    &:last-child {
        border-bottom: none;
    }
`;

const PropName = styled.span`
    font-family: ${typography.fontFamily.code};
    font-size: ${typography.fontSize[100]};
    color: ${colors.cyan[500]};
    min-width: 70px;
`;

const PropType = styled.span`
    font-family: ${typography.fontFamily.code};
    font-size: ${typography.fontSize[50]};
    color: ${({ theme }) => theme.subtleText};
`;

const VariantCount = styled.div`
    font-size: ${typography.fontSize[100]};
    color: ${({ color }) => color};
    font-weight: ${typography.fontWeight.semibold};
    margin-top: 0.75rem;
    padding: 0.5rem;
    background: ${({ color }) => color}10;
    border-radius: 4px;
    text-align: center;
`;

export const VariantVsProperty = () => (
    <ComparisonWrapper>
        <ComparisonSide>
            <ComparisonHeader>
                <StatusDot color={colors.coral[500]} />
                <ComparisonTitle color={colors.coral[500]}>Variant Approach</ComparisonTitle>
            </ComparisonHeader>
            <VariantItem>ButtonDefault</VariantItem>
            <VariantItem>ButtonOutline</VariantItem>
            <VariantItem>ButtonDefaultSmall</VariantItem>
            <VariantItem>ButtonOutlineSmall</VariantItem>
            <VariantItem>ButtonDefaultDisabled</VariantItem>
            <VariantItem>ButtonOutlineDisabled</VariantItem>
            <VariantItem>ButtonDefaultSmallDisabled</VariantItem>
            <VariantItem>ButtonOutlineSmallDisabled</VariantItem>
            <VariantCount color={colors.coral[500]}>
                2 variants x 2 sizes x 2 states = 8 components
            </VariantCount>
        </ComparisonSide>

        <ComparisonSide>
            <ComparisonHeader>
                <StatusDot color={colors.emerald[500]} />
                <ComparisonTitle color={colors.emerald[500]}>Property Approach</ComparisonTitle>
            </ComparisonHeader>
            <PropComponentBox>
                <PropComponentName>{"<Button />"}</PropComponentName>
                <PropRow>
                    <PropName>variant</PropName>
                    <PropType>"default" | "outline" | "ghost"</PropType>
                </PropRow>
                <PropRow>
                    <PropName>size</PropName>
                    <PropType>"sm" | "md" | "lg"</PropType>
                </PropRow>
                <PropRow>
                    <PropName>disabled</PropName>
                    <PropType>boolean</PropType>
                </PropRow>
                <PropRow>
                    <PropName>icon</PropName>
                    <PropType>ReactNode</PropType>
                </PropRow>
            </PropComponentBox>
            <VariantCount color={colors.emerald[500]}>
                1 component, infinite combinations
            </VariantCount>
        </ComparisonSide>
    </ComparisonWrapper>
);


// ===== 3. Token Flow Diagram =====
const TokenFlowWrapper = styled(DiagramContainer)`
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 0;
    padding: 2.5rem 2rem;
`;

const TokenLayer = styled.div`
    width: 100%;
    max-width: 500px;
    background: ${({ color }) => color}10;
    border: 2px solid ${({ color }) => color};
    border-radius: 8px;
    padding: 1.25rem;
    text-align: center;
`;

const TokenLayerTitle = styled.div`
    font-weight: ${typography.fontWeight.bold};
    font-size: ${typography.fontSize[200]};
    color: ${({ color }) => color};
    margin-bottom: 0.25rem;
`;

const TokenLayerDesc = styled.div`
    font-size: ${typography.fontSize[100]};
    color: ${({ theme }) => theme.subtleText};
`;

const TokenExamples = styled.div`
    display: flex;
    gap: 0.5rem;
    justify-content: center;
    flex-wrap: wrap;
    margin-top: 0.75rem;
`;

const TokenChip = styled.span`
    font-family: ${typography.fontFamily.code};
    font-size: ${typography.fontSize[50]};
    background: ${({ color }) => color}20;
    color: ${({ color }) => color};
    padding: 3px 10px;
    border-radius: 4px;
`;

const FlowArrow = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    padding: 0.5rem 0;
    color: ${({ theme }) => theme.subtleText};
    font-size: 20px;
`;

export const TokenFlow = () => (
    <TokenFlowWrapper>
        <DiagramLabel>Three-Layer Architecture</DiagramLabel>
        <div style={{ height: '1.5rem' }} />

        <TokenLayer color={colors.amber[500]}>
            <TokenLayerTitle color={colors.amber[500]}>Tokens</TokenLayerTitle>
            <TokenLayerDesc>Raw design values</TokenLayerDesc>
            <TokenExamples>
                <TokenChip color={colors.amber[600]}>blue-500: #0099CC</TokenChip>
                <TokenChip color={colors.amber[600]}>space-400: 16px</TokenChip>
                <TokenChip color={colors.amber[600]}>radius-200: 8px</TokenChip>
            </TokenExamples>
        </TokenLayer>

        <FlowArrow>&#8595;</FlowArrow>

        <TokenLayer color={colors.indigo[500]}>
            <TokenLayerTitle color={colors.indigo[500]}>Decisions</TokenLayerTitle>
            <TokenLayerDesc>Semantic application of tokens</TokenLayerDesc>
            <TokenExamples>
                <TokenChip color={colors.indigo[500]}>primary: blue-500</TokenChip>
                <TokenChip color={colors.indigo[500]}>cardPadding: space-400</TokenChip>
                <TokenChip color={colors.indigo[500]}>cardRadius: radius-200</TokenChip>
            </TokenExamples>
        </TokenLayer>

        <FlowArrow>&#8595;</FlowArrow>

        <TokenLayer color={colors.emerald[500]}>
            <TokenLayerTitle color={colors.emerald[500]}>Components</TokenLayerTitle>
            <TokenLayerDesc>What users interact with</TokenLayerDesc>
            <TokenExamples>
                <TokenChip color={colors.emerald[500]}>{"<Card />"}</TokenChip>
                <TokenChip color={colors.emerald[500]}>{"<Button />"}</TokenChip>
                <TokenChip color={colors.emerald[500]}>{"<Input />"}</TokenChip>
            </TokenExamples>
        </TokenLayer>
    </TokenFlowWrapper>
);


// ===== 4. AI Pipeline Diagram =====
const PipelineWrapper = styled(DiagramContainer)`
    display: flex;
    align-items: center;
    justify-content: center;
    row-gap: 1.5rem;
    column-gap: 0;
    flex-wrap: wrap;
    padding: 2.5rem 2rem;
`;

const PipelineStep = styled.div`
    background: ${({ color }) => color}12;
    border: 2px solid ${({ color }) => color};
    border-radius: 8px;
    padding: 1rem 1.5rem;
    text-align: center;
    min-width: 160px;
    flex-shrink: 0;
`;

const PipelineStepTitle = styled.div`
    font-weight: ${typography.fontWeight.bold};
    font-size: ${typography.fontSize[200]};
    color: ${({ color }) => color};
    margin-bottom: 4px;
`;

const PipelineStepDesc = styled.div`
    font-size: ${typography.fontSize[50]};
    color: ${({ theme }) => theme.subtleText};
`;

const PipelineArrow = styled.div`
    color: ${({ theme }) => theme.subtleText};
    font-size: 24px;
    padding: 0 1rem;
    flex-shrink: 0;

    @media (max-width: 640px) {
        transform: rotate(90deg);
        padding: 0.75rem 0;
    }
`;

export const AIPipeline = () => (
    <PipelineWrapper>
        <DiagramLabel style={{ width: '100%', textAlign: 'center', marginBottom: '0.5rem' }}>
            AI-Ready Component Architecture
        </DiagramLabel>
        <PipelineStep color={colors.amber[500]}>
            <PipelineStepTitle color={colors.amber[500]}>Structured Data</PipelineStepTitle>
            <PipelineStepDesc>JSON / API response</PipelineStepDesc>
        </PipelineStep>

        <PipelineArrow>&#8594;</PipelineArrow>

        <PipelineStep color={colors.indigo[500]}>
            <PipelineStepTitle color={colors.indigo[500]}>Component API</PipelineStepTitle>
            <PipelineStepDesc>Props-driven interface</PipelineStepDesc>
        </PipelineStep>

        <PipelineArrow>&#8594;</PipelineArrow>

        <PipelineStep color={colors.emerald[500]}>
            <PipelineStepTitle color={colors.emerald[500]}>Rendered UI</PipelineStepTitle>
            <PipelineStepDesc>Composable output</PipelineStepDesc>
        </PipelineStep>
    </PipelineWrapper>
);
